import { get, readable, writable } from "svelte/store";
import type { PrivateSpaceEvent, SpaceEvent } from "@workadventure/messages";
import { localUserStore } from "../Connection/LocalUserStore";
import { gameManager } from "../Phaser/Game/GameManager";
import { availabilityStatusStore } from "../Stores/MediaStore";
import LL from "../../i18n/i18n-svelte";
import type { SpaceUserExtended } from "./SpaceInterface";

export const localSpaceUser = (name?: string): SpaceUserExtended => {
    return {
        isLogged: localUserStore.isLogged(),
        availabilityStatus: get(availabilityStatusStore),
        roomName: undefined,
        visitCardUrl: undefined,
        tags: [],
        cameraState: false,
        microphoneState: false,
        screenSharingState: true,
        megaphoneState: false,
        uuid: localUserStore.getLocalUser()?.uuid ?? "",
        chatID: localUserStore.getChatId() ?? undefined,
        showVoiceIndicator: true,
        spaceUserId: "local",
        name: name ?? get(LL).camera.my.nameTag(),
        playUri: "local",
        color: "local",
        jitsiParticipantId: undefined,
        characterTextures: [],
        attendeesState: false,
        // CASCADAI PATCH: your OWN portrait, on your own tile.
        //
        // Space.ts's extendSpaceUser covers everyone ELSE. The local user takes
        // this entirely separate path -- CurrentPlayer.pictureStore, which is a
        // snapshot of the Phaser woka sprite -- so patching only the remote
        // path meant other people saw your portrait and you never did.
        //
        // Same rule as there: the woka remains the fallback, and a 404 (nobody
        // has minted a portrait for you) is the normal case.
        pictureStore: readable<string | undefined>(undefined, (set) => {
            let unsubscribe: (() => void) | undefined;
            const woka = () => {
                unsubscribe = gameManager
                    .getCurrentGameScene()
                    .CurrentPlayer.pictureStore.subscribe((pictureStore) => {
                        set(pictureStore);
                    });
            };
            const uuid = localUserStore.getLocalUser()?.uuid ?? "";
            if (!uuid) {
                woka();
            } else {
                const portrait =
                    "https://htbaa.cascadai.org/room-api/portrait?u=" +
                    encodeURIComponent(uuid);
                const probe = new Image();
                probe.onload = () => set(portrait);
                probe.onerror = () => woka();
                probe.src = portrait;
            }
            return () => {
                if (unsubscribe) unsubscribe();
            };
        }),
        emitPrivateEvent: (message: NonNullable<PrivateSpaceEvent["event"]>) => {
            throw new Error("should not be called");
        },
        space: {
            emitPublicMessage: (message: NonNullable<SpaceEvent["event"]>) => {
                throw new Error("should not be called");
            },
            canAskToMuteAudioOrTurnOffVideo: writable(false),
        },
        reactiveUser: {
            spaceUserId: "",
            playUri: "",
            roomName: "",
            name: writable(localUserStore.getName() ?? ""),
            color: writable("local"),
            characterTextures: writable([]),
            showVoiceIndicator: writable(true),
            availabilityStatus: writable(get(availabilityStatusStore)),
            isLogged: writable(localUserStore.isLogged()),
            visitCardUrl: writable(undefined),
            tags: writable([]),
            cameraState: writable(false),
            microphoneState: writable(false),
            attendeesState: writable(false),
            screenSharingState: writable(true),
            megaphoneState: writable(false),
            jitsiParticipantId: writable(undefined),
            uuid: writable(localUserStore.getLocalUser()?.uuid ?? ""),
            chatID: writable(localUserStore.getChatId() ?? undefined),
        },
    };
};
